import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import { Pencil, Trash2, ArrowUp, ArrowDown, Plus, X, GripVertical, Eye, EyeOff, History } from 'lucide-react';
import './AdminPastries.css';

const AdminPastries = () => {
    const [pastries, setPastries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPastry, setEditingPastry] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        nom: '',
        description: '',
        prix: '',
        disponible: true,
        stock: 0,
        afficherStock: true,
        lowStockThreshold: 5,
        estSouvenir: false,
        dateTexte: '',
        photo: null
    });

    useEffect(() => {
        fetchPastries();
    }, []);

    const fetchPastries = async () => {
        setLoading(true);
        try {
            // Sort by 'ordre' ascending. 
            // Warning: Ensure 'ordre' field exists in PocketBase collection
            const records = await pb.collection('patisseries').getList(1, 100, {
                sort: '+ordre',
            });
            setPastries(records.items);
        } catch (err) {
            console.error("Error fetching pastries:", err);
            // Fallback if 'ordre' doesn't exist, try sorting by created
            try {
                const records = await pb.collection('patisseries').getList(1, 100, {
                    sort: '-created',
                });
                setPastries(records.items);
            } catch (retryErr) {
                console.error("Retry failed:", retryErr);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        if (type === 'file') {
            setFormData({ ...formData, [name]: files[0] });
        } else if (type === 'checkbox') {
            setFormData({ ...formData, [name]: checked });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const openModal = (pastry = null) => {
        if (pastry) {
            setEditingPastry(pastry);
            setFormData({
                nom: pastry.nom,
                description: pastry.description,
                prix: pastry.prix,
                disponible: pastry.disponible,
                stock: pastry.stock || 0,
                afficherStock: pastry.hasOwnProperty('afficherStock') ? pastry.afficherStock : true,
                lowStockThreshold: pastry.lowStockThreshold || 5,
                estSouvenir: pastry.estSouvenir || false,
                dateTexte: pastry.dateTexte || '',
                photo: null // New photo file, if any
            });
        } else {
            setEditingPastry(null);
            setFormData({
                nom: '',
                description: '',
                prix: '',
                disponible: true,
                stock: 0,
                afficherStock: true,
                lowStockThreshold: 5,
                estSouvenir: false,
                dateTexte: '',
                photo: null
            });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingPastry(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const data = new FormData();
            data.append('nom', formData.nom);
            data.append('description', formData.description);
            data.append('prix', formData.prix);
            data.append('disponible', formData.disponible);
            data.append('stock', formData.stock);
            data.append('afficherStock', formData.afficherStock);
            data.append('lowStockThreshold', formData.lowStockThreshold);
            data.append('estSouvenir', formData.estSouvenir);
            data.append('dateTexte', formData.dateTexte);

            if (formData.photo) {
                data.append('photo', formData.photo);
            }

            if (editingPastry) {
                await pb.collection('patisseries').update(editingPastry.id, data);
            } else {
                // Determine new order (last + 1)
                // Note: This relies on 'ordre' existing. If not, it just saves without it.
                // ideally fetch max order first.
                const lastItem = pastries[pastries.length - 1];
                const newOrder = lastItem && lastItem.ordre ? lastItem.ordre + 1 : pastries.length + 1;
                data.append('ordre', newOrder);

                await pb.collection('patisseries').create(data);
            }

            closeModal();
            fetchPastries();
        } catch (err) {
            console.error("Error saving pastry:", err);
            alert("Erreur lors de la sauvegarde.");
        }
    };

    const handleToggleVisibility = async (pastry) => {
        try {
            const newStatus = !pastry.disponible;
            await pb.collection('patisseries').update(pastry.id, { disponible: newStatus });
            // Update local state for immediate feedback
            setPastries(prev => prev.map(p => p.id === pastry.id ? { ...p, disponible: newStatus } : p));
        } catch (err) {
            console.error("Error toggling visibility:", err);
            alert("Erreur lors de la modification de la visibilité.");
        }
    };

    const handleToggleSouvenir = async (pastry) => {
        try {
            const newStatus = !pastry.estSouvenir;
            await pb.collection('patisseries').update(pastry.id, { estSouvenir: newStatus });
            setPastries(prev => prev.map(p => p.id === pastry.id ? { ...p, estSouvenir: newStatus } : p));
        } catch (err) {
            console.error("Error toggling souvenir status:", err);
            alert("Erreur lors de la modification du statut Souvenir.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cette pâtisserie ?")) {
            try {
                await pb.collection('patisseries').delete(id);
                fetchPastries();
            } catch (err) {
                console.error("Error deleting:", err);
            }
        }
    };

    const handleMove = async (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === pastries.length - 1) return;

        const otherIndex = direction === 'up' ? index - 1 : index + 1;
        const currentItem = pastries[index];
        const otherItem = pastries[otherIndex];

        // Swap order values
        const currentOrder = currentItem.ordre || index + 1;
        const otherOrder = otherItem.ordre || otherIndex + 1;

        try {
            await Promise.all([
                pb.collection('patisseries').update(currentItem.id, { ordre: otherOrder }),
                pb.collection('patisseries').update(otherItem.id, { ordre: currentOrder })
            ]);
            await fetchPastries();
        } catch (err) {
            console.error("Error reordering:", err);
        }
    };

    // Drag and Drop Handlers
    const onDragStart = (e, index) => {
        setDraggedItem(pastries[index]);
        e.dataTransfer.effectAllowed = "move";
        // Ghost image setting if needed
        e.dataTransfer.setData("text/html", e.target.parentNode);
        e.target.style.opacity = '0.5';
    };

    const onDragOver = (e, index) => {
        e.preventDefault();
        if (pasteIndex !== index) {
            setDragOverItem(index);
        }
    };

    let pasteIndex = null;
    const onDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDragOverItem(null);
    };

    const onDrop = async (e, targetIndex) => {
        e.preventDefault();
        const sourceIndex = pastries.findIndex(p => p.id === draggedItem.id);
        if (sourceIndex === targetIndex) return;

        const newPastries = [...pastries];
        const [removed] = newPastries.splice(sourceIndex, 1);
        newPastries.splice(targetIndex, 0, removed);

        // Optimistic UI update
        setPastries(newPastries);

        try {
            // Bulk update order values
            // To be efficient, we update all items' order field to match their new index
            const updates = newPastries.map((p, idx) =>
                pb.collection('patisseries').update(p.id, { ordre: idx + 1 })
            );
            await Promise.all(updates);
            // Refresh to ensure sync
            fetchPastries();
        } catch (err) {
            console.error("Error updating order after drop:", err);
            fetchPastries(); // Revert on error
        }
    };

    if (loading) return <div>Chargement...</div>;

    return (
        <div className="admin-pastries-container">
            <div className="admin-tools">
                <button className="btn" onClick={() => openModal()}>
                    <Plus size={16} style={{ marginRight: '5px' }} /> Ajouter une pâtisserie
                </button>
            </div>

            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }}>
                <table className="pastries-table" style={{ minWidth: '650px' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>Image</th>
                            <th>Nom</th>
                            <th>Prix</th>
                            <th>Stock</th>
                            <th>Visible</th>
                            <th>Souvenir</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pastries.map((pastry, index) => (
                            <tr
                                key={pastry.id}
                                draggable
                                onDragStart={(e) => onDragStart(e, index)}
                                onDragOver={(e) => onDragOver(e, index)}
                                onDragEnd={onDragEnd}
                                onDrop={(e) => onDrop(e, index)}
                                className={dragOverItem === index ? 'drag-over' : ''}
                            >
                                <td className="drag-handle-cell">
                                    <GripVertical size={18} className="drag-handle-icon" />
                                </td>
                                <td>
                                    {pastry.photo ? (
                                        <img
                                            src={pb.files.getURL(pastry, pastry.photo, { thumb: '100x100' })}
                                            alt={pastry.nom}
                                            className="pastry-thumbnail"
                                        />
                                    ) : (
                                        <div className="pastry-thumbnail" style={{ background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No img</div>
                                    )}
                                </td>
                                <td>{pastry.nom}</td>
                                <td>{pastry.prix} €</td>
                                <td>
                                    <span style={{
                                        fontWeight: 'bold',
                                        color: pastry.stock <= (pastry.lowStockThreshold || 5) ? '#e74c3c' : '#27ae60'
                                    }}>
                                        {pastry.stock || 0}
                                    </span>
                                    {pastry.afficherStock && <span title="Affiché au client" style={{ marginLeft: '5px', opacity: 0.5 }}>👁️</span>}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    <button
                                        className="action-btn"
                                        onClick={() => handleToggleVisibility(pastry)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: pastry.disponible ? '#27ae60' : '#bdc3c7',
                                            padding: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto',
                                            transition: 'transform 0.2s ease'
                                        }}
                                        title={pastry.disponible ? "Publié (Cliquer pour masquer)" : "Masqué (Cliquer pour publier)"}
                                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        {pastry.disponible ? <Eye size={20} /> : <EyeOff size={20} />}
                                    </button>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    <button
                                        className="action-btn"
                                        onClick={() => handleToggleSouvenir(pastry)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: pastry.estSouvenir ? '#e67e22' : '#bdc3c7',
                                            padding: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto',
                                            transition: 'transform 0.2s ease'
                                        }}
                                        title={pastry.estSouvenir ? "Est un Souvenir (Cliquer pour remettre en vente)" : "Pas un Souvenir (Cliquer pour archiver)"}
                                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <History size={20} />
                                    </button>
                                </td>
                                <td>
                                    <button className="action-btn btn-move" onClick={() => handleMove(index, 'up')} disabled={index === 0}>
                                        <ArrowUp size={14} />
                                    </button>
                                    <button className="action-btn btn-move" onClick={() => handleMove(index, 'down')} disabled={index === pastries.length - 1}>
                                        <ArrowDown size={14} />
                                    </button>
                                    <button className="action-btn btn-edit" onClick={() => openModal(pastry)}>
                                        <Pencil size={14} />
                                    </button>
                                    <button className="action-btn btn-delete" onClick={() => handleDelete(pastry.id)}>
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {pastries.length === 0 && (
                            <tr>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>Aucune pâtisserie trouvée.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingPastry ? 'Modifier' : 'Ajouter'} une pâtisserie</h2>
                            <button className="close-btn" onClick={closeModal} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><X /></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Nom</label>
                                <input
                                    type="text"
                                    name="nom"
                                    value={formData.nom}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Prix (€)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    name="prix"
                                    value={formData.prix}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>Stock actuel</label>
                                    <input
                                        type="number"
                                        name="stock"
                                        value={formData.stock}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </div>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>Seuil d'alerte</label>
                                    <input
                                        type="number"
                                        name="lowStockThreshold"
                                        value={formData.lowStockThreshold}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </div>
                            </div>
                            <div className="form-group" style={{ display: 'flex', gap: '20px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        name="disponible"
                                        checked={formData.disponible}
                                        onChange={handleInputChange}
                                        style={{ width: 'auto', marginRight: '10px' }}
                                    />
                                    Disponible sur le site
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        name="afficherStock"
                                        checked={formData.afficherStock}
                                        onChange={handleInputChange}
                                        style={{ width: 'auto', marginRight: '10px' }}
                                    />
                                    Afficher le stock au client
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        name="estSouvenir"
                                        checked={formData.estSouvenir}
                                        onChange={handleInputChange}
                                        style={{ width: 'auto', marginRight: '10px' }}
                                    />
                                    Est un Souvenir (Archives)
                                </label>
                            </div>
                            {formData.estSouvenir && (
                                <div className="form-group" style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px', border: '1px solid #eee' }}>
                                    <label>Texte personnalisé de la date (ex: "Printemps 2023", "Collection de Noël")</label>
                                    <input
                                        type="text"
                                        name="dateTexte"
                                        value={formData.dateTexte}
                                        onChange={handleInputChange}
                                        placeholder="Si vide, affiche le mois et l'année de création par défaut"
                                    />
                                </div>
                            )}
                            <div className="form-group">
                                <label>Photo</label>
                                <input
                                    type="file"
                                    name="photo"
                                    onChange={handleInputChange}
                                    accept="image/*"
                                />
                                {editingPastry && editingPastry.photo && !formData.photo && (
                                    <small>Laisser vide pour conserver la photo actuelle.</small>
                                )}
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn" style={{ background: '#999' }} onClick={closeModal}>Annuler</button>
                                <button type="submit" className="btn">Sauvegarder</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPastries;
